"use client";
import { useCallback, useEffect, useId, useRef, useState } from "react";

/**
 * LoginDialog — Password-first Verwaltungs-Login mit Magic-Link-Fallback.
 *
 * State-Machine (status):
 *   idle                       Default; user kann tippen
 *   submitting-password        POST /api/v1/auth/login läuft
 *   submitting-magic-link      POST /api/v1/auth/magic-link/request läuft
 *   sent                       Magic-Link verschickt (Success-View)
 *   error                      Inline-Fehlermeldung sichtbar
 *
 * Mode-Switch (mode):
 *   password     Default-Eingabe (Email + Password). Submit hits /auth/login.
 *   magic-link   Fallback (Account hat noch kein Passwort ODER User hat "Passwort
 *                vergessen" geklickt). Submit hits /auth/magic-link/request.
 *
 * Server-Error-Mapping (POST /auth/login):
 *   401 + code=password_not_set       Auto-Switch in magic-link-Mode mit Info
 *   401 + code=invalid_credentials    Inline-Error "E-Mail oder Passwort falsch."
 *   429                               Inline-Error "Zu viele Anmeldeversuche."
 *   andere                            generischer Netzwerk-Fehler
 *
 * Erfolg (200): window.location.assign('/admin') — der Server hat das Session-
 * Cookie via Set-Cookie gesetzt, Next-Page-Load liest es. Kein client-side Routing,
 * weil /admin als Server-Component das Cookie sofort braucht.
 */

type Status =
  | 'idle'
  | 'submitting-password'
  | 'submitting-magic-link'
  | 'sent'
  | 'error';

type Mode = 'password' | 'magic-link';

interface ErrorBody {
  error?: {
    code?: string;
    message?: string;
  };
}

export function LoginDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<Mode>('password');
  const [status, setStatus] = useState<Status>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [infoMsg, setInfoMsg] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  // Vor dem Öffnen merken wir uns das auslösende Element, damit Tastatur/
  // Screenreader-User nach Esc oder Backdrop-Click wieder dort landen, wo
  // sie waren (VerwaltungEntry-Button). Sonst springt der Fokus zum body-
  // Anfang — WCAG 2.4.3 Focus Order Verletzung.
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const errorId = useId();
  const infoId = useId();

  // Esc to close + focus trap (nur Elemente, die wirklich fokussierbar sind)
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const nodes = root.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
        );
        // Filter: disabled, aria-hidden, hidden (offsetParent null) raus —
        // sonst springt focus() auf disabled-Buttons zurück zum body und
        // die Trap bricht.
        const focusable = Array.from(nodes).filter((el) => {
          if (el.hasAttribute('disabled')) return false;
          if (el.getAttribute('aria-hidden') === 'true') return false;
          if (el.getAttribute('type') === 'hidden') return false;
          if ((el as HTMLElement).offsetParent === null && getComputedStyle(el).position !== 'fixed') return false;
          return true;
        });
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    // Initial focus on email input after animation
    const t = setTimeout(() => emailInputRef.current?.focus(), 150);
    // Prevent body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Snapshot des Trigger-Buttons für Cleanup (Ref könnte im Closure
    // veralten, wenn das Dialog mehrfach geöffnet wird — daher Snapshot).
    const trigger = previousFocusRef.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      setStatus('idle');
      setMode('password');
      setEmail('');
      setPassword('');
      setErrMsg(null);
      setInfoMsg(null);
      // Fokus zurück zum Trigger (best-effort; falls Element nicht mehr im
      // DOM, ignorieren wir still).
      if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [open, onClose]);

  // Wenn der Mode auf magic-link wechselt, fokus auf das Email-Feld
  // zurücksetzen (Password-Feld ist nicht mehr sichtbar). Wir warten einen
  // Tick, damit React die Re-Render durch hat.
  useEffect(() => {
    if (!open) return undefined;
    if (mode === 'magic-link' && status !== 'sent') {
      const t = setTimeout(() => emailInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    // Wenn wir zurück zu password springen, Password-Feld fokussieren wenn
    // die Email bereits eingegeben ist (Convenience).
    if (mode === 'password' && email.trim().length > 0) {
      const t = setTimeout(() => passwordInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [mode, open, status, email]);

  const switchToMagicLink = useCallback((info?: string) => {
    setMode('magic-link');
    setStatus('idle');
    setErrMsg(null);
    setInfoMsg(info ?? null);
    setPassword('');
  }, []);

  const switchToPassword = useCallback(() => {
    setMode('password');
    setStatus('idle');
    setErrMsg(null);
    setInfoMsg(null);
  }, []);

  const submitPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (status === 'submitting-password' || status === 'submitting-magic-link') return;
      const trimmed = email.trim();
      if (!trimmed || !trimmed.includes('@')) {
        setErrMsg('Bitte gib eine gültige E-Mail-Adresse ein.');
        setStatus('error');
        return;
      }
      if (!password) {
        setErrMsg('Bitte gib Dein Passwort ein.');
        setStatus('error');
        return;
      }
      setStatus('submitting-password');
      setErrMsg(null);
      setInfoMsg(null);
      try {
        const res = await fetch('/api/v1/auth/login', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ email: trimmed, password }),
        });
        if (res.ok) {
          // Cookie ist gesetzt — harter Redirect, damit Server-Components
          // auf /admin das Cookie sofort sehen.
          window.location.assign('/admin');
          return;
        }
        if (res.status === 429) {
          setErrMsg('Zu viele Anmeldeversuche. Bitte später erneut versuchen.');
          setStatus('error');
          return;
        }
        if (res.status === 401) {
          // Code aus dem Error-Body lesen (api-contracts: { error: { code, message } }).
          let code: string | undefined;
          try {
            const body = (await res.json()) as ErrorBody;
            code = body?.error?.code;
          } catch {
            // Body nicht parsebar — generischer Fall unten.
          }
          if (code === 'password_not_set') {
            switchToMagicLink(
              'Für diesen Account ist noch kein Passwort gesetzt. Wir schicken Dir einen einmaligen Login-Link.',
            );
            return;
          }
          // Default: invalid_credentials (oder unbekannter 401-Code)
          setErrMsg('E-Mail oder Passwort falsch. Bitte erneut versuchen.');
          setStatus('error');
          return;
        }
        setErrMsg('Anmeldung fehlgeschlagen. Bitte später erneut versuchen.');
        setStatus('error');
      } catch {
        setErrMsg('Netzwerk-Fehler. Bitte später erneut versuchen.');
        setStatus('error');
      }
    },
    [email, password, status, switchToMagicLink],
  );

  const submitMagicLink = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (status === 'submitting-password' || status === 'submitting-magic-link') return;
      const trimmed = email.trim();
      if (!trimmed || !trimmed.includes('@')) {
        setErrMsg('Bitte gib eine gültige E-Mail-Adresse ein.');
        setStatus('error');
        return;
      }
      setStatus('submitting-magic-link');
      setErrMsg(null);
      try {
        const res = await fetch('/api/v1/auth/magic-link/request', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ email: trimmed }),
        });
        if (res.status === 429) {
          setErrMsg('Zu viele Anfragen. Bitte später erneut versuchen.');
          setStatus('error');
          return;
        }
        if (!res.ok && res.status !== 200) {
          setErrMsg('Anfrage konnte nicht gesendet werden. Bitte später erneut versuchen.');
          setStatus('error');
          return;
        }
        setStatus('sent');
      } catch {
        setErrMsg('Netzwerk-Fehler. Bitte später erneut versuchen.');
        setStatus('error');
      }
    },
    [email, status],
  );

  if (!open) return null;

  const isSubmitting = status === 'submitting-password' || status === 'submitting-magic-link';

  // Buttons/Inputs styling-tokens — bewusst inline, weil layout.tsx als
  // CSS-Single-Source-of-Truth alle var(--*) Tokens definiert. Tailwind ist
  // im Repo nicht gewired (siehe layout.tsx Kommentar).
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    borderRadius: 8,
    border: '1px solid var(--border-subtle)',
    color: 'var(--ink-text)',
    outline: 'none',
    background: '#fff',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    color: 'var(--ink-text)',
  };

  return (
    <div
      className="lp-modal-backdrop is-open"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="lp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink-text)' }}>
            {status === 'sent'
              ? 'Link verschickt'
              : mode === 'password'
                ? 'Verwaltung anmelden'
                : 'Magic-Link anfordern'}
          </h2>
          <button
            type="button"
            aria-label="Schließen"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-muted)', fontSize: 24, lineHeight: 1, padding: 4 }}
          >
            ×
          </button>
        </div>

        {status === 'sent' ? (
          <div role="status" aria-live="polite">
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--fcp-green-soft)', color: 'var(--fcp-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-muted)', textAlign: 'center', lineHeight: 1.5 }}>
              Wenn Deine Adresse bei uns hinterlegt ist, findest Du in Kürze einen Magic-Link an{' '}
              <strong style={{ color: 'var(--ink-text)' }}>{email}</strong> in Deinem Posteingang.
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{ marginTop: 24, width: '100%', padding: '10px 16px', background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: 8, color: 'var(--ink-text)', fontSize: 14, cursor: 'pointer' }}
            >
              Schließen
            </button>
          </div>
        ) : mode === 'password' ? (
          <>
            <p style={{ margin: '0 0 20px 0', fontSize: 14, color: 'var(--ink-muted)', lineHeight: 1.5 }}>
              Melde Dich mit Deiner E-Mail-Adresse und Deinem Passwort an.
            </p>
            <form onSubmit={submitPassword} noValidate>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor={`${titleId}-email`} style={labelStyle}>
                  E-Mail-Adresse
                </label>
                <input
                  ref={emailInputRef}
                  id={`${titleId}-email`}
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (status === 'error') { setStatus('idle'); setErrMsg(null); } }}
                  required
                  disabled={isSubmitting}
                  aria-describedby={errMsg ? errorId : undefined}
                  style={inputStyle}
                />
              </div>
              <div>
                <label htmlFor={`${titleId}-password`} style={labelStyle}>
                  Passwort
                </label>
                <input
                  ref={passwordInputRef}
                  id={`${titleId}-password`}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (status === 'error') { setStatus('idle'); setErrMsg(null); } }}
                  required
                  disabled={isSubmitting}
                  aria-describedby={errMsg ? errorId : undefined}
                  style={inputStyle}
                />
              </div>
              {errMsg && (
                <p id={errorId} role="alert" style={{ margin: '12px 0 0', color: '#E5484D', fontSize: 13 }}>
                  {errMsg}
                </p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="fcp-btn-primary"
                style={{ marginTop: 18, width: '100%', padding: '12px 20px', fontSize: 15, borderRadius: 8 }}
                aria-busy={status === 'submitting-password'}
              >
                {status === 'submitting-password' ? 'Wird angemeldet …' : 'Anmelden'}
              </button>
              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => switchToMagicLink()}
                  style={{ background: 'transparent', border: 'none', padding: 0, color: 'var(--fcp-green)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Passwort vergessen? Magic-Link senden →
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 20px 0', fontSize: 14, color: 'var(--ink-muted)', lineHeight: 1.5 }}>
              {infoMsg ?? 'Wir schicken Dir einen einmaligen Login-Link an Deine E-Mail-Adresse.'}
            </p>
            <form onSubmit={submitMagicLink} noValidate>
              <label htmlFor={`${titleId}-ml-email`} style={labelStyle}>
                E-Mail-Adresse
              </label>
              <input
                ref={emailInputRef}
                id={`${titleId}-ml-email`}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (status === 'error') { setStatus('idle'); setErrMsg(null); } }}
                required
                disabled={isSubmitting}
                aria-describedby={errMsg ? errorId : infoMsg ? infoId : undefined}
                style={inputStyle}
              />
              {errMsg && (
                <p id={errorId} role="alert" style={{ margin: '8px 0 0', color: '#E5484D', fontSize: 13 }}>
                  {errMsg}
                </p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="fcp-btn-primary"
                style={{ marginTop: 18, width: '100%', padding: '12px 20px', fontSize: 15, borderRadius: 8 }}
                aria-busy={status === 'submitting-magic-link'}
              >
                {status === 'submitting-magic-link' ? 'Wird gesendet …' : 'Login-Link senden'}
              </button>
              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={switchToPassword}
                  style={{ background: 'transparent', border: 'none', padding: 0, color: 'var(--ink-muted)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  ← Zurück zu Passwort-Login
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
